/*
 This file is part of GNU Taler
 (C) 2022-2024, 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import {
  AbsoluteTime,
  ObservabilityEventType,
  TalerError,
  assertUnreachable,
} from "@gnu-taler/taler-util";
import {
  Footer,
  Header,
  Loading,
  ToastBanner,
  useBankCoreApiContext,
  useCommonPreferences,
  useNotificationContext,
  useOptionalNavigationContext,
  useRenderErrorReport,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { ComponentChildren, Fragment, VNode, h } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { useSettingsContext } from "../context/settings.js";
import {
  revalidateAccountDetails,
  useAccountDetails,
} from "../hooks/account.js";
import { useBankState } from "../hooks/bank-state.js";
import {
  getAllBooleanPreferences,
  getLabelForPreferences,
  usePreferences,
} from "../hooks/preferences.js";
import { useSessionState } from "../hooks/session.js";
import { DemoBanner } from "../components/DemoBanner.js";
import { OperationError } from "../components/OperationError.js";
import { COREBANK_API_BASE_URL_OVERRIDE_KEY } from "../developer-settings.js";

const TALER_SCREEN_ID = 103;

const GIT_HASH = typeof __GIT_HASH__ !== "undefined" ? __GIT_HASH__ : undefined;
const VERSION = typeof __VERSION__ !== "undefined" ? __VERSION__ : undefined;
const REVOCATION_WARNING_KEY = "bank-logout-revocation-unconfirmed";

function AccountDetailsRetry(): VNode {
  const { i18n } = useTranslationContext();
  return (
    <button
      type="button"
      class="font-semibold underline"
      onClick={() => void revalidateAccountDetails()}
    >
      <i18n.Translate>Try again</i18n.Translate>
    </button>
  );
}

BankFrame.SCREEN_ID = TALER_SCREEN_ID;
export function BankFrame({
  children,
  account,
  navigation,
  publicAccountsUrl,
}: {
  account?: string;
  navigation?: ComponentChildren;
  publicAccountsUrl?: string;
  children: ComponentChildren;
}): VNode {
  const { i18n } = useTranslationContext();
  const session = useSessionState();
  const settings = useSettingsContext();
  const showDemoBanner = settings.enableDemoHeader ?? false;
  const showPublicAccounts = settings.showPublicAccounts ?? false;
  const [, , resetBankState] = useBankState();
  const path = useOptionalNavigationContext()?.path;
  const { clear: clearNotifications, clearErrors } = useNotificationContext();
  const previousPath = useRef(path);
  const clearErrorsRef = useRef(clearErrors);
  clearErrorsRef.current = clearErrors;
  useEffect(() => {
    if (previousPath.current === path) return;
    previousPath.current = path;
    clearErrorsRef.current();
  }, [path]);
  const d = useBankCoreApiContext();
  const config = d === undefined ? undefined : d.config;
  const authenticator = d === undefined ? undefined : d.lib.bank;
  const [revocationWarning, setRevocationWarning] = useState(
    () =>
      typeof window !== "undefined" &&
      window.sessionStorage.getItem(REVOCATION_WARNING_KEY) === "true",
  );
  const [settingsOpen, setSettingsOpen] = useState(false);

  async function logOut(): Promise<void> {
    let revocationConfirmed = false;
    try {
      if (session.state.status === "loggedIn" && authenticator) {
        const result = await Promise.race([
          authenticator.deleteAccessToken(
            session.state.username,
            session.state.token,
          ),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error("revocation timeout")), 5_000),
          ),
        ]);
        revocationConfirmed = result.type === "ok";
      }
    } catch {
      revocationConfirmed = false;
    } finally {
      clearNotifications();
      session.logOut();
      resetBankState();
      setRevocationWarning(!revocationConfirmed);
      if (typeof window !== "undefined") {
        if (revocationConfirmed) {
          window.sessionStorage.removeItem(REVOCATION_WARNING_KEY);
        } else {
          window.sessionStorage.setItem(REVOCATION_WARNING_KEY, "true");
        }
        window.location.hash = "#/login";
      }
    }
  }

  const failed = useRenderErrorReport({
    hash: __GIT_HASH__,
    version: __VERSION__,
  });

  return (
    <div
      class="min-h-full flex flex-col m-0 bg-background text-onBackground"
      style="min-height: 100vh;"
    >
      {showDemoBanner ? (
        <DemoBanner sites={settings.topNavSites ?? {}} />
      ) : (
        <div class={`bg-brand ${account ? "" : "pb-32"}`}>
          <Header
            title={config?.bank_name ?? "Bank"}
            iconLinkURL={settings.iconLinkURL ?? "#"}
            profileURL={undefined}
            notificationURL={undefined}
            onLogout={undefined}
            showMenu={false}
            sites={[]}
            backgroundClass="bg-brand"
          />
        </div>
      )}

      <main class={account || showDemoBanner ? "flex-1" : "-mt-32 flex-1"}>
        <div
          class={`mx-auto max-w-7xl px-4 pb-4 sm:px-6 lg:px-8 ${account || showDemoBanner ? "pt-6" : ""}`}
        >
          <div
            class={
              account
                ? undefined
                : "rounded-lg bg-white px-5 py-6 shadow sm:px-6"
            }
          >
            {account ? (
              <header class="mb-4 flex items-center justify-between gap-4">
                <h1 class="text-2xl font-bold tracking-tight text-brand">
                  <WelcomeAccount account={account} />
                </h1>
                <button
                  type="button"
                  class="shrink-0 rounded-md border border-outlineVariant px-3 py-2 text-sm font-semibold text-secondary hover:border-primary/40 hover:bg-primary/10 hover:text-primaryDark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                  onClick={() => void logOut()}
                >
                  <i18n.Translate>Sign out</i18n.Translate>
                </button>
              </header>
            ) : undefined}
            {navigation}
            <div class="mb-4 space-y-2 empty:hidden">
              <ToastBanner compact messageType="info" />
              {revocationWarning && (
                <div
                  class="flex items-start justify-between gap-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
                  role="alert"
                >
                  <span>
                    <i18n.Translate>
                      You were signed out locally, but the bank could not
                      confirm that the server token was revoked. Close other
                      sessions or sign in again to review active tokens.
                    </i18n.Translate>
                  </span>
                  <button
                    type="button"
                    class="shrink-0 font-semibold underline"
                    onClick={() => {
                      setRevocationWarning(false);
                      if (typeof window !== "undefined") {
                        window.sessionStorage.removeItem(
                          REVOCATION_WARNING_KEY,
                        );
                      }
                    }}
                  >
                    <i18n.Translate>Dismiss</i18n.Translate>
                  </button>
                </div>
              )}
            </div>
            {failed ? <OperationError class="mb-4" /> : children}
          </div>
        </div>
      </main>

      <AppActivity />

      <BankSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onSignOut={
          session.state.status === "loggedIn"
            ? async () => {
                setSettingsOpen(false);
                await logOut();
              }
            : undefined
        }
      />

      <Footer
        testingUrlKey={COREBANK_API_BASE_URL_OVERRIDE_KEY}
        GIT_HASH={GIT_HASH}
        VERSION={VERSION}
        variant="demo"
        actions={
          <div class="flex items-center gap-3">
            {showPublicAccounts && publicAccountsUrl ? (
              <a
                href={publicAccountsUrl}
                class="text-xs font-semibold text-gray-500 hover:text-primaryDark hover:underline"
              >
                <i18n.Translate>Public accounts</i18n.Translate>
              </a>
            ) : undefined}
            <button
              type="button"
              class="rounded-full p-2 text-gray-500 hover:bg-primary/10 hover:text-primaryDark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              aria-label={i18n.str`Open interface preferences`}
              title={i18n.str`Interface preferences`}
              onClick={() => setSettingsOpen(true)}
            >
              <svg
                class="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.592c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827a1.125 1.125 0 0 0-.43 1.012v.254c-.008.378.153.738.43.992l1.003.827c.424.35.534.956.26 1.431l-1.296 2.247a1.125 1.125 0 0 1-1.37.49l-1.217-.456a1.125 1.125 0 0 0-1.075.124 6.57 6.57 0 0 1-.22.127 1.125 1.125 0 0 0-.645.87l-.213 1.281c-.09.542-.56.94-1.11.94h-2.592c-.55 0-1.02-.398-1.11-.94l-.213-1.281a1.125 1.125 0 0 0-.645-.87 6.52 6.52 0 0 1-.22-.127 1.125 1.125 0 0 0-1.075-.124l-1.217.456a1.125 1.125 0 0 1-1.37-.49L3.568 15.38a1.125 1.125 0 0 1 .26-1.431l1.003-.827c.277-.254.438-.614.43-.992v-.254a1.125 1.125 0 0 0-.43-1.012l-1.003-.827a1.125 1.125 0 0 1-.26-1.431l1.296-2.247a1.125 1.125 0 0 1 1.37-.49l1.217.456c.355.133.751.072 1.075-.124.073-.044.146-.087.22-.127.332-.184.582-.496.645-.87l.213-1.281Z"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                />
              </svg>
            </button>
          </div>
        }
      />
    </div>
  );
}

function BankSettingsDialog({
  open,
  onClose,
  onSignOut,
}: {
  open: boolean;
  onClose(): void;
  onSignOut?: () => void | Promise<void>;
}): VNode {
  const { i18n } = useTranslationContext();
  const [{ showDebugInfo }, updateCommonPreference] = useCommonPreferences();
  const [preferences, updatePreference] = usePreferences();

  useEffect(() => {
    if (!open || typeof window === "undefined") return;
    const closeOnEscape = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose, open]);

  if (!open) return <Fragment />;

  return (
    <dialog
      ref={(element) => {
        if (element && !element.open) element.showModal();
      }}
      aria-modal="true"
      aria-labelledby="bank-settings-title"
      class="fixed inset-0 z-50 size-auto max-h-none max-w-none overflow-y-auto bg-transparent p-4"
    >
      <div class="fixed inset-0 bg-secondary/45" aria-hidden="true" />
      <div class="relative flex min-h-full items-center justify-center">
        <section class="w-full max-w-md rounded-xl bg-white p-6 text-onBackground shadow-xl">
          <div class="flex items-start justify-between gap-4">
            <div>
              <h2 id="bank-settings-title" class="text-lg font-semibold">
                <i18n.Translate>Interface preferences</i18n.Translate>
              </h2>
              <p class="mt-1 text-sm text-gray-600">
                <i18n.Translate>
                  Choose how this bank interface behaves on this browser.
                </i18n.Translate>
              </p>
            </div>
            <button
              type="button"
              class="rounded p-1 text-xl leading-none text-gray-500 hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              aria-label={i18n.str`Close interface preferences`}
              onClick={onClose}
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>

          <ul class="mt-6 divide-y divide-gray-200">
            {getAllBooleanPreferences().map((preference) => {
              const isOn = !!preferences[preference];
              const labelId = `settings-preference-${preference}`;
              return (
                <li
                  key={preference}
                  class="flex items-center justify-between gap-4 py-4"
                >
                  <span id={labelId} class="text-sm font-medium">
                    {getLabelForPreferences(preference, i18n)}
                  </span>
                  <PreferenceSwitch
                    name={`${preference} switch`}
                    labelId={labelId}
                    enabled={isOn}
                    onChange={() => updatePreference(preference, !isOn)}
                  />
                </li>
              );
            })}
            <li class="flex items-center justify-between gap-4 py-4">
              <span id="settings-debug-label" class="text-sm font-medium">
                <i18n.Translate>Show debug information</i18n.Translate>
              </span>
              <PreferenceSwitch
                name="debug switch"
                labelId="settings-debug-label"
                enabled={showDebugInfo}
                onChange={() =>
                  updateCommonPreference("showDebugInfo", !showDebugInfo)
                }
              />
            </li>
          </ul>

          <div class="mt-6 flex justify-end gap-3">
            {onSignOut ? (
              <button
                type="button"
                class="mr-auto rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-onBackground shadow-sm hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
                onClick={() => void onSignOut()}
              >
                <i18n.Translate>Sign out</i18n.Translate>
              </button>
            ) : undefined}
            <button
              type="button"
              class="rounded-md bg-primary px-3 py-2 text-sm font-semibold text-onPrimary shadow-sm hover:bg-primaryHover hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              onClick={onClose}
            >
              <i18n.Translate>Done</i18n.Translate>
            </button>
          </div>
        </section>
      </div>
    </dialog>
  );
}

function PreferenceSwitch({
  name,
  labelId,
  enabled,
  onChange,
}: {
  name: string;
  labelId: string;
  enabled: boolean;
  onChange(): void;
}): VNode {
  return (
    <button
      type="button"
      name={name}
      data-enabled={enabled}
      class="relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-primary transition-colors data-[enabled=false]:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
      role="switch"
      aria-checked={enabled}
      aria-labelledby={labelId}
      onClick={onChange}
    >
      <span
        aria-hidden="true"
        data-enabled={enabled}
        class="pointer-events-none inline-block h-5 w-5 translate-x-5 transform rounded-full bg-white shadow ring-0 transition data-[enabled=false]:translate-x-0"
      />
    </button>
  );
}

Wait.SCREEN_ID = TALER_SCREEN_ID;
function Wait({ class: clazz }: { class?: string }): VNode {
  return (
    <Fragment>
      <style>{`
      .animated-loader {
        display: inline-block;
        --b: 5px; 
        border-radius: 50%;
        aspect-ratio: 1;
        padding: 1px;
        background: conic-gradient(#0000 10%,#0042b3) content-box;
        -webkit-mask:
          repeating-conic-gradient(#0000 0deg,#000 1deg 20deg,#0000 21deg 36deg),
          radial-gradient(farthest-side,#0000 calc(100% - var(--b) - 1px),#000 calc(100% - var(--b)));
        -webkit-mask-composite: destination-in;
                mask-composite: intersect;
        animation:spinning-loader 1s infinite steps(10);
      }
      @keyframes spinning-loader {to{transform: rotate(1turn)}}    
    `}</style>
      <div class={`animated-loader ${clazz}`} />
    </Fragment>
  );
}

AppActivity.SCREEN_ID = TALER_SCREEN_ID;
function AppActivity(): VNode {
  const { i18n } = useTranslationContext();
  const [lastEvent, setLastEvent] = useState<{
    url: string;
    id: string;
    when: AbsoluteTime;
  }>();
  const [status, setStatus] = useState<"ok" | "fail">();
  const d = useBankCoreApiContext();
  const onBackendActivity = !d ? undefined : d.onActivity;
  const cancelRequest = !d ? undefined : d.cancelRequest;
  const [{ showDebugInfo }] = useCommonPreferences();
  useEffect(() => {
    // console.log("ASDASDS", onBackendActivity)
    if (!showDebugInfo) return;
    if (!onBackendActivity) return;
    return onBackendActivity((ev) => {
      switch (ev.type) {
        case ObservabilityEventType.HttpFetchStart: {
          setLastEvent(ev);
          setStatus(undefined);
          return;
        }
        case ObservabilityEventType.HttpFetchFinishError: {
          setStatus("fail");
          return;
        }
        case ObservabilityEventType.HttpFetchFinishSuccess: {
          setStatus("ok");
          return;
        }
        /**
         * all of these are ignored
         */
        case ObservabilityEventType.DbQueryStart:
        case ObservabilityEventType.DbQueryFinishSuccess:
        case ObservabilityEventType.DbQueryFinishError:
        case ObservabilityEventType.RequestStart:
        case ObservabilityEventType.RequestFinishSuccess:
        case ObservabilityEventType.RequestFinishError:
        case ObservabilityEventType.TaskStart:
        case ObservabilityEventType.TaskStop:
        case ObservabilityEventType.TaskReset:
        case ObservabilityEventType.ShepherdTaskResult:
        case ObservabilityEventType.DeclareTaskDependency:
        case ObservabilityEventType.CryptoStart:
        case ObservabilityEventType.CryptoFinishSuccess:
        case ObservabilityEventType.CryptoFinishError:
        case ObservabilityEventType.Message:
        case ObservabilityEventType.DeclareConcernsTransaction:
          return;
        default: {
          assertUnreachable(ev);
        }
      }
    });
  });
  if (!showDebugInfo || !lastEvent) return <Fragment />;
  return (
    <div
      data-status={status}
      class="fixed z-40 bottom-0 w-full ease-in-out delay-1000 transition-transform data-[status=ok]:scale-y-0"
    >
      <div
        data-status={status}
        class="mx-auto w-4/5 center flex p-1 bg-gray-300 m-1 data-[status=fail]:bg-red-200 data-[status=ok]:bg-green-200 "
      >
        {!status ? <Wait class="w-6 h-6" /> : <div class="w-6 h-6" />}

        <p class="ml-2 my-auto text-sm text-gray-500">{lastEvent.url}</p>
        {!status ? (
          <button
            type="button"
            onClick={() => {
              if (cancelRequest) cancelRequest(lastEvent.id);
            }}
          >
            <i18n.Translate>Cancel</i18n.Translate>
          </button>
        ) : undefined}
      </div>
    </div>
  );
}

WelcomeAccount.SCREEN_ID = TALER_SCREEN_ID;
function WelcomeAccount({ account }: { account: string }): VNode {
  const { i18n } = useTranslationContext();
  const result = useAccountDetails(account);
  if (!result) {
    return <Loading />;
  }
  if (result instanceof TalerError) {
    return <AccountDetailsRetry />;
  }
  if (result.type === "fail") {
    return <i18n.Translate>Welcome</i18n.Translate>;
  }
  return (
    <span>
      <i18n.Translate>
        Welcome, <span class="whitespace-nowrap">{result.body.name}</span>
      </i18n.Translate>
    </span>
  );
}
